import { useId, useState, type DragEvent } from 'react';
import { Aviso, Botao, Modal, Selo } from './ui';
import { useEnviarSpeds } from '../hooks/useEscrita';
import { ErroDaApi } from '../paginas/ErroDaApi';

/**
 * Envio de arquivos SPED para um cliente existente.
 *
 * ## O que a tela mostra depois do envio
 *
 * Três listas, e as três importam: os cálculos criados, os **avisos** do parser
 * (registro faltando, valor inesperado) e os arquivos **recusados** com o
 * motivo. O legado devolvia só a lista boa, e um arquivo que não entrou sumia em
 * silêncio — quem enviou doze e teve onze processados não tinha como saber qual
 * faltou.
 *
 * O parse acontece no servidor, sempre. Registro fiscal é regra de domínio com
 * teste; uma segunda implementação no navegador seria a divergência do RN-078
 * esperando para acontecer.
 */
export function EnvioDeSped(props: {
  readonly clienteId: string;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}): JSX.Element {
  const [arquivos, setArquivos] = useState<readonly File[]>([]);
  const [recusadosLocal, setRecusadosLocal] = useState<readonly string[]>([]);
  const [arrastando, setArrastando] = useState(false);
  const idEntrada = useId();
  const envio = useEnviarSpeds(props.clienteId);

  const receber = (lista: FileList | null): void => {
    if (lista === null) return;

    const aceitos: File[] = [];
    const fora: string[] = [];
    for (const a of Array.from(lista)) {
      // RN-613 — o SPED é sempre `.txt`. O `type` do navegador vem vazio em
      // vários casos, então a extensão manda.
      if (a.name.toLowerCase().endsWith('.txt')) aceitos.push(a);
      else fora.push(a.name);
    }

    setRecusadosLocal(fora);
    setArquivos((atuais) => {
      const existentes = new Set(atuais.map((a) => `${a.name}:${a.size}`));
      const novos = aceitos.filter((a) => !existentes.has(`${a.name}:${a.size}`));
      // O backend aceita 24 por envio; cortar aqui evita o 400 depois do upload.
      return [...atuais, ...novos].slice(0, 24);
    });
  };

  const fechar = (): void => {
    envio.reset();
    setArquivos([]);
    setRecusadosLocal([]);
    props.aoFechar();
  };

  const resultado = envio.data;

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Enviar arquivos SPED"
      descricao="Um arquivo por competência, no .txt exportado do SPED Fiscal."
      largura="larga"
      rodape={
        resultado === undefined ? (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              disabled={arquivos.length === 0}
              carregando={envio.isPending}
              onClick={() => envio.mutate(arquivos)}
            >
              {envio.isPending ? 'Processando…' : `Enviar ${arquivos.length || ''}`}
            </Botao>
          </>
        ) : (
          <Botao onClick={fechar}>Concluir</Botao>
        )
      }
    >
      {resultado !== undefined ? (
        <div className="flex flex-col gap-4">
          <Aviso tom="positivo" titulo={`${resultado.linhas.length} competência(s) processada(s)`}>
            Os cálculos já aparecem na aba de cálculos do cliente.
          </Aviso>

          {resultado.recusados.length > 0 && (
            <Aviso tom="atencao" titulo={`${resultado.recusados.length} arquivo(s) recusado(s)`}>
              <ul className="mt-2 flex flex-col gap-1">
                {resultado.recusados.map((r) => (
                  <li key={r.arquivo}>
                    <span className="valor-fiscal text-xs">{r.arquivo}</span> — {r.motivo}
                  </li>
                ))}
              </ul>
            </Aviso>
          )}

          {resultado.avisos.length > 0 && (
            <Aviso tom="informacao" titulo="Avisos da leitura">
              <ul className="mt-2 flex flex-col gap-1">
                {resultado.avisos.map((a, i) => (
                  <li key={`${a.arquivo}-${a.codigo}-${i}`}>
                    <span className="valor-fiscal text-xs">{a.codigo}</span> — {a.mensagem}
                  </li>
                ))}
              </ul>
            </Aviso>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div
            onDragOver={(e: DragEvent<HTMLDivElement>) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e: DragEvent<HTMLDivElement>) => {
              e.preventDefault();
              setArrastando(false);
              receber(e.dataTransfer.files);
            }}
            className={`rounded-painel border border-dashed px-6 py-10 text-center transition-colors ${
              arrastando ? 'border-acento-ciano bg-superficie-vidro' : 'border-borda'
            }`}
          >
            <p className="text-sm text-conteudo-secundario">
              Arraste os arquivos <span className="valor-fiscal text-xs">.txt</span> aqui
            </p>
            <p className="mt-1 text-xs text-conteudo-sutil">ou</p>

            {/* `sr-only` mantém o input focável pelo teclado; o label o aciona. */}
            <input
              id={idEntrada}
              type="file"
              multiple
              accept=".txt,text/plain"
              className="sr-only"
              onChange={(e) => {
                receber(e.target.files);
                e.target.value = '';
              }}
            />
            <label
              htmlFor={idEntrada}
              className="mt-3 inline-flex h-10 cursor-pointer items-center rounded-card border border-borda bg-superficie-vidro px-4 text-sm"
            >
              Selecionar arquivos
            </label>
          </div>

          {recusadosLocal.length > 0 && (
            <Aviso tom="atencao" titulo="Ignorados por não serem .txt">
              {recusadosLocal.join(', ')}
            </Aviso>
          )}

          {arquivos.length > 0 && (
            <ul className="flex flex-col gap-2">
              {arquivos.map((a) => (
                <li
                  key={`${a.name}:${a.size}`}
                  className="flex items-center justify-between gap-3 rounded-card border border-borda px-4 py-2"
                >
                  <span className="truncate text-sm">{a.name}</span>
                  <span className="flex shrink-0 items-center gap-3">
                    <Selo semPonto>{Math.max(1, Math.round(a.size / 1024))} KB</Selo>
                    <Botao
                      variante="sutil"
                      tamanho="pequeno"
                      aria-label={`Remover ${a.name}`}
                      onClick={() =>
                        setArquivos((atuais) =>
                          atuais.filter((x) => !(x.name === a.name && x.size === a.size)),
                        )
                      }
                    >
                      Remover
                    </Botao>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {envio.isError && <ErroDaApi titulo="O envio falhou" erro={envio.error} />}
        </div>
      )}
    </Modal>
  );
}
